/**
 * Ссылки в обычном тексте (сообщения чата) без HTML: текст режется на куски
 * «текст / ссылка», а элементы из них собирает React — он же экранирует текст.
 *
 * Ссылкой становится только адрес, начинающийся с http://, https:// или www.
 * Остальные схемы (javascript:, data: и т. п.) и HTML-теги остаются текстом.
 */

import { safeHttpUrl } from './url'

export type LinkifyPart =
  | { type: 'text'; text: string }
  /** text — адрес так, как его написали; href — проверенный адрес для перехода */
  | { type: 'link'; text: string; href: string }

/** Кандидат в адрес: от схемы или www. до пробела, угловой скобки или кавычки */
const URL_CANDIDATE = /(?:https?:\/\/|www\.)[^\s<>"«»“”]+/giu

const WWW_PREFIX = 'www.'

/*
 * Кандидат, приклеенный к слову, ссылкой не считается: «xhttps://…»,
 * «mail@www.…», «sub.www.…». Проверяем символ перед совпадением вручную:
 * lookbehind в регулярке Safari до 16.4 не разбирает, и на нём не загрузился
 * бы весь бандл.
 */
const WORD_BEFORE_SCHEME = /[\p{L}\p{N}_]/u
const WORD_BEFORE_WWW = /[\p{L}\p{N}_@.\-/]/u

/** Знаки, которыми заканчивается фраза сразу после адреса */
const TRAILING_PUNCTUATION = /[.,;:!?'…»]$/u

function countOf(value: string, char: string): number {
  return value.split(char).length - 1
}

/**
 * Хвост кандидата без знаков препинания и без непарной закрывающей скобки:
 * «(см. https://x.ru/p).» → «https://x.ru/p», а «…/Foo_(bar)» остаётся целым.
 */
function trimTrailing(candidate: string): string {
  let result = candidate

  for (;;) {
    if (TRAILING_PUNCTUATION.test(result)) {
      result = result.slice(0, -1)
      continue
    }
    if (result.endsWith(')') && countOf(result, ')') > countOf(result, '(')) {
      result = result.slice(0, -1)
      continue
    }

    return result
  }
}

function append(parts: LinkifyPart[], part: LinkifyPart): void {
  if (part.type === 'text') {
    if (part.text === '') {
      return
    }

    const last = parts[parts.length - 1]
    if (last !== undefined && last.type === 'text') {
      last.text += part.text
      return
    }
  }

  parts.push(part)
}

/** Разбор текста на куски «текст / ссылка»; склейка кусков даёт исходный текст */
export function linkify(text: string): LinkifyPart[] {
  const parts: LinkifyPart[] = []
  let position = 0

  for (const match of text.matchAll(URL_CANDIDATE)) {
    const raw = match[0]
    const index = match.index ?? 0
    const isWww = raw.slice(0, WWW_PREFIX.length).toLowerCase() === WWW_PREFIX
    // Длина «www.» или «http(s)://» — без неё за адресом ничего нет
    const prefixLength = isWww ? WWW_PREFIX.length : raw.indexOf('//') + 2
    const candidate = trimTrailing(raw)
    const before = index > 0 ? text[index - 1] : ''
    const gluedToWord = before !== '' && (isWww ? WORD_BEFORE_WWW : WORD_BEFORE_SCHEME).test(before)

    // Пустой адрес («www.», «https://.») и адрес внутри слова остаются текстом
    const href =
      gluedToWord || candidate.length <= prefixLength
        ? null
        : safeHttpUrl(isWww ? `https://${candidate}` : candidate)

    if (href === null) {
      continue
    }

    append(parts, { type: 'text', text: text.slice(position, index) })
    parts.push({ type: 'link', text: candidate, href })
    position = index + candidate.length
  }

  append(parts, { type: 'text', text: text.slice(position) })

  return parts
}
